import * as React from 'react'
import { noop } from 'lodash'
import { useState } from 'react'
import t from './strings'
import { ArchiveAccountState, PrivacyMode as Mode } from '../../../models/types'
import {
  Text,
  StackChildren,
  Icon,
  InlineChildren,
} from '@doordash/design-language'
import Button from '@doordash/component-button'
import { Interpolate } from '@doordash/lib-i18n'
import { isWebview } from '../../../utilities/userAgent'
import { loadUrl } from '../../../webview'
import i18n from '../../../i18n'
import { i18nKeys } from 'translationTypes'
import * as api from '../../../api'

type Props = {
  archiveRequestId?: string
  archiveAccountState?: ArchiveAccountState
  archiveRequestDate?: string
  archiveExpiryDate?: string
  archiveDownloadLink?: string
  onModeChange?: (mode: Mode) => void
}

const ArchiveAccountSection: React.FC<Props> = ({
  archiveRequestId,
  archiveAccountState,
  archiveRequestDate,
  archiveExpiryDate,
  archiveDownloadLink,
  onModeChange,
}) => {
  const [resendButtonState, setResendButtonState] = useState(
    Button.States.Default
  )

  const bodyText = React.useMemo(() => {
    if (archiveAccountState === ArchiveAccountState.NoStatus) {
      return <Text styles={Text.Styles.Body1}>{t.body.noStatus()}</Text>
    }

    if (archiveAccountState === ArchiveAccountState.Pending) {
      return (
        <Text styles={Text.Styles.Body1}>
          <Interpolate
            i18n={i18n}
            i18nKey={
              i18nKeys.privacy_manageAccount_archiveAccount_body_processing
            }
            values={{ archiveRequestDate }}
            components={[<Text display={Text.DisplayModes.Inline} />]}
          />
        </Text>
      )
    }

    return (
      <Text styles={Text.Styles.Body1}>
        <Interpolate
          i18n={i18n}
          i18nKey={i18nKeys.privacy_manageAccount_archiveAccount_body_ready}
          values={{ archiveRequestDate, archiveExpiryDate }}
          components={[<Text display={Text.DisplayModes.Inline} />]}
        />
      </Text>
    )
  }, [archiveAccountState, archiveRequestDate, archiveExpiryDate])

  const handlePendingArchiveOnClick = async () => {
    if (archiveAccountState === ArchiveAccountState.NoStatus) {
      onModeChange(Mode.RTI)
    } else if (archiveAccountState === ArchiveAccountState.Ready) {
      if (isWebview()) {
        loadUrl(archiveDownloadLink)
      } else {
        window.location.assign(archiveDownloadLink)
      }
    }
  }

  const handleResendEmailOnClick = async () => {
    try {
      setResendButtonState(Button.States.Loading)
      await api.resendDasherRTIEmail(archiveRequestId)
      setResendButtonState(Button.States.Success)
    } catch (e) {
      setResendButtonState(Button.States.Error)
    }
  }

  return (
    <InlineChildren size={InlineChildren.Sizes.XxSmall}>
      {archiveAccountState !== ArchiveAccountState.NoStatus && (
        <Icon accessibilityLabel="" type={Icon.Types.DownloadLine} />
      )}
      <StackChildren size={StackChildren.Sizes.XxSmall}>
        <Text styles={Text.Styles.Label1Emphasis}>
          {archiveAccountState === ArchiveAccountState.NoStatus
            ? t.header.noStatus()
            : t.header.hasStatus()}
        </Text>
        {bodyText}
        <Button
          type={Button.Types.Tertiary}
          size={Button.Sizes.Small}
          isInline
          isDisabled={archiveAccountState === ArchiveAccountState.Pending}
          onClick={handlePendingArchiveOnClick}
        >
          {archiveAccountState === ArchiveAccountState.Ready
            ? t.button.download()
            : t.button.request()}
        </Button>
        {archiveAccountState === ArchiveAccountState.Ready && (
          <Button
            type={Button.Types.Tertiary}
            size={Button.Sizes.Small}
            state={resendButtonState}
            isInline
            onClick={handleResendEmailOnClick}
          >
            {t.button.send()}
          </Button>
        )}
      </StackChildren>
    </InlineChildren>
  )
}

ArchiveAccountSection.defaultProps = {
  onModeChange: noop,
}

export default ArchiveAccountSection
